import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ModalContext = createContext(null);
export const useValuation = () => useContext(ModalContext);
export const useAppModals = () => useContext(ModalContext);

const V_EMPTY = {
  name: "", email: "", phone: "", address: "",
  postcode: "", property_type: "", bedrooms: "", message: "",
};
const R_EMPTY = {
  name: "", email: "", phone: "", areas: "",
  max_budget: "", bedrooms: "", move_date: "", message: "",
};
const E_EMPTY = { name: "", email: "", phone: "", message: "", service: "" };

function Success({ title, text, onClose }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <CheckCircle2 size={56} className="text-[#35a4a4]" />
      <h3 className="mt-4 font-display text-2xl font-600 text-[#16323f]">{title}</h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600">{text}</p>
      <button
        onClick={onClose}
        className="mt-6 bg-[#16323f] px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]"
      >
        Close
      </button>
    </div>
  );
}

export function ValuationProvider({ children }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  // valuation state
  const [vOpen, setVOpen] = useState(false);
  const [vForm, setVForm] = useState(V_EMPTY);
  const [vLoading, setVLoading] = useState(false);
  const [vDone, setVDone] = useState(false);
  // register state
  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState(R_EMPTY);
  const [rLoading, setRLoading] = useState(false);
  const [rDone, setRDone] = useState(false);
  // enquiry state
  const [eOpen, setEOpen] = useState(false);
  const [eForm, setEForm] = useState(E_EMPTY);
  const [eLoading, setELoading] = useState(false);
  const [eDone, setEDone] = useState(false);

  const openValuation = () => { navigate("/valuation"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const openRegister = () => { setRDone(false); setRForm(R_EMPTY); setROpen(true); };
  const openEnquiry = (service = "") => { setEDone(false); setEForm({ ...E_EMPTY, service }); setEOpen(true); };

  const vSet = (k, v) => setVForm((f) => ({ ...f, [k]: v }));
  const rSet = (k, v) => setRForm((f) => ({ ...f, [k]: v }));
  const eSet = (k, v) => setEForm((f) => ({ ...f, [k]: v }));

  const eSubmit = async (ev) => {
    ev.preventDefault();
    if (!eForm.name || !eForm.email || !eForm.phone) {
      toast({ title: "Missing details", description: "Please fill in your name, email and phone.", variant: "destructive" });
      return;
    }
    setELoading(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: eForm.name,
        email: eForm.email,
        phone: eForm.phone,
        subject: eForm.service ? `Landlord enquiry \u2013 ${eForm.service}` : "Landlord enquiry",
        message: eForm.message,
      });
      setEDone(true);
      toast({ title: "Enquiry sent", description: "Thanks \u2014 we\u2019ll be in touch shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setELoading(false); }
  };

  const vSubmit = async (e) => {
    e.preventDefault();
    if (!vForm.name || !vForm.email || !vForm.phone || !vForm.address) {
      toast({ title: "Missing details", description: "Please fill in name, email, phone and property address.", variant: "destructive" });
      return;
    }
    setVLoading(true);
    try {
      await axios.post(`${API}/valuations`, vForm);
      setVDone(true);
      toast({ title: "Enquiry received", description: "Thanks \u2014 Richard will be in touch shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setVLoading(false); }
  };

  const rSubmit = async (e) => {
    e.preventDefault();
    if (!rForm.name || !rForm.email || !rForm.phone) {
      toast({ title: "Missing details", description: "Please fill in your name, email and phone.", variant: "destructive" });
      return;
    }
    setRLoading(true);
    try {
      await axios.post(`${API}/registrations`, rForm);
      setRDone(true);
      toast({ title: "Alerts are on", description: "We'll email you the moment a matching property lands." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setRLoading(false); }
  };

  return (
    <ModalContext.Provider value={{ openValuation, openRegister, openEnquiry }}>
      {children}

      {/* Valuation dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          {vDone ? (
            <Success
              title="Thank you!"
              text="Your valuation request has been received. Richard and the New Space team will get back to you shortly."
              onClose={() => setVOpen(false)}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">
                  Request a Valuation
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  {"Tell us about your property and we\u2019ll be in touch with a free, no-obligation valuation."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={vSubmit} className="mt-2 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="v-name">Full name *</Label>
                    <Input id="v-name" value={vForm.name} onChange={(e) => vSet("name", e.target.value)} placeholder="Jane Smith" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="v-phone">Phone *</Label>
                    <Input id="v-phone" value={vForm.phone} onChange={(e) => vSet("phone", e.target.value)} placeholder="07123 456789" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v-email">Email *</Label>
                  <Input id="v-email" type="email" value={vForm.email} onChange={(e) => vSet("email", e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="v-address">Property address *</Label>
                    <Input id="v-address" value={vForm.address} onChange={(e) => vSet("address", e.target.value)} placeholder="1 Kingsland Road" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="v-postcode">Postcode</Label>
                    <Input id="v-postcode" value={vForm.postcode} onChange={(e) => vSet("postcode", e.target.value)} placeholder="E8 4DG" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Property type</Label>
                    <Select value={vForm.property_type} onValueChange={(v) => vSet("property_type", v)}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Flat">Flat</SelectItem>
                        <SelectItem value="House">House</SelectItem>
                        <SelectItem value="House Terraced">House Terraced</SelectItem>
                        <SelectItem value="Studio">Studio</SelectItem>
                        <SelectItem value="Commercial">Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Bedrooms</Label>
                    <Select value={vForm.bedrooms} onValueChange={(v) => vSet("bedrooms", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Studio">Studio</SelectItem>
                        {[1, 2, 3, 4, 5, 6].map((b) => (<SelectItem key={b} value={String(b)}>{b}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v-message">Message</Label>
                  <Textarea id="v-message" rows={3} value={vForm.message} onChange={(e) => vSet("message", e.target.value)} placeholder="Anything else we should know?" />
                </div>
                <button type="submit" disabled={vLoading}
                  className="group flex w-full items-center justify-center gap-2 bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                  {vLoading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (
                    <>Submit request <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Register dialog */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          {rDone ? (
            <Success
              title="Alerts are on!"
              text="You're all set. We'll email you the moment a property matching your requirements lands with New Space."
              onClose={() => setROpen(false)}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">
                  Register for property alerts
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  {"Tell us what you\u2019re looking for and we\u2019ll alert you the moment a matching property lands."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={rSubmit} className="mt-2 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="r-name">Full name *</Label>
                    <Input id="r-name" value={rForm.name} onChange={(e) => rSet("name", e.target.value)} placeholder="Jane Smith" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="r-phone">Phone *</Label>
                    <Input id="r-phone" value={rForm.phone} onChange={(e) => rSet("phone", e.target.value)} placeholder="07123 456789" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="r-email">Email *</Label>
                  <Input id="r-email" type="email" value={rForm.email} onChange={(e) => rSet("email", e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="r-areas">Preferred areas</Label>
                  <Input id="r-areas" value={rForm.areas} onChange={(e) => rSet("areas", e.target.value)} placeholder="e.g. London Fields, Dalston" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Max budget (pcm)</Label>
                    <Select value={rForm.max_budget} onValueChange={(v) => rSet("max_budget", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {["1,000", "1,250", "1,500", "1,750", "2,000", "2,500", "3,000", "3,500", "4,000", "5,000+"].map((p) => (
                          <SelectItem key={p} value={p}>{`\u00a3${p}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Min bedrooms</Label>
                    <Select value={rForm.bedrooms} onValueChange={(v) => rSet("bedrooms", v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Studio">Studio</SelectItem>
                        {[1, 2, 3, 4, 5].map((b) => (<SelectItem key={b} value={String(b)}>{b}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="r-move">Ideal move-in date</Label>
                  <Input id="r-move" type="date" value={rForm.move_date} onChange={(e) => rSet("move_date", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="r-message">Message</Label>
                  <Textarea id="r-message" rows={3} value={rForm.message} onChange={(e) => rSet("message", e.target.value)} placeholder="Tell us more about what you need..." />
                </div>
                <button type="submit" disabled={rLoading}
                  className="group flex w-full items-center justify-center gap-2 bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                  {rLoading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (
                    <>Set up my alerts <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry dialog */}
      <Dialog open={eOpen} onOpenChange={setEOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
          {eDone ? (
            <Success
              title="Enquiry sent"
              text="Thanks for your enquiry. A member of the New Space team will be in touch shortly to discuss the right service and a bespoke quote for you."
              onClose={() => setEOpen(false)}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">
                  {eForm.service ? `Enquire \u2013 ${eForm.service}` : "Make an enquiry"}
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  {"Tell us a little about your property and we\u2019ll be in touch with a bespoke quote tailored to your needs."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={eSubmit} className="mt-2 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="e-name">Full name *</Label>
                    <Input id="e-name" value={eForm.name} onChange={(ev) => eSet("name", ev.target.value)} placeholder="Jane Smith" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="e-phone">Phone *</Label>
                    <Input id="e-phone" value={eForm.phone} onChange={(ev) => eSet("phone", ev.target.value)} placeholder="07123 456789" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="e-email">Email *</Label>
                  <Input id="e-email" type="email" value={eForm.email} onChange={(ev) => eSet("email", ev.target.value)} placeholder="you@email.com" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="e-message">Message</Label>
                  <Textarea id="e-message" rows={4} value={eForm.message} onChange={(ev) => eSet("message", ev.target.value)} placeholder="Tell us about your property and what you need..." />
                </div>
                <button type="submit" disabled={eLoading}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                  {eLoading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (
                    <>Send enquiry <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  );
}
